/**
 * flight-chart.js
 * Draws the SVG flight path for every [data-flight-chart] element on the
 * page, and wires up its two toggles:
 *   - hand:  right (RHBH, default) / left (LHBH) — mirrors the path
 *   - skill: beginner / intermediate / advanced — nudges the flight
 *            numbers to approximate how the disc flies for that arm speed
 *
 * Base flight numbers come from data-speed/glide/turn/fade attributes,
 * set server-side by flight-chart.liquid from the product's metafields.
 * No build step, no dependencies.
 */
(function () {
  var SKILL_ADJUST = {
    beginner:     { speed: -1, glide: -1, turn: 0.5, fade: 1 },
    intermediate: { speed: 0,  glide: 0,  turn: 0,   fade: 0 },
    advanced:     { speed: 1,  glide: 1,  turn: -0.5, fade: -1 }
  };

  function adjustForSkill(base, skill) {
    var adj = SKILL_ADJUST[skill] || SKILL_ADJUST.intermediate;
    return {
      speed: Math.max(1, base.speed + adj.speed),
      glide: Math.max(1, base.glide + adj.glide),
      turn: base.turn + adj.turn,
      fade: Math.max(0, base.fade + adj.fade)
    };
  }

  // Speed+Glide → estimated distance (ft), piecewise-linear interpolation
  // over the reference points. Refine this table any time by editing it —
  // nothing else needs to change.
  var DISTANCE_TABLE = [
    [6, 235],
    [10, 280],
    [12, 320],
    [14, 360],
    [18, 425]
  ];
  var FC_MAX_FEET = 500;

  function estimateDistanceFt(speed, glide) {
    var sum = speed + glide;
    var t = DISTANCE_TABLE;
    if (sum <= t[0][0]) return t[0][1];
    if (sum >= t[t.length - 1][0]) return t[t.length - 1][1];
    for (var i = 0; i < t.length - 1; i++) {
      var a = t[i], b = t[i + 1];
      if (sum >= a[0] && sum <= b[0]) {
        var f = (sum - a[0]) / (b[0] - a[0]);
        return Math.round(a[1] + f * (b[1] - a[1]));
      }
    }
    return t[t.length - 1][1];
  }

  /**
   * The path is one continuous formula sampled into a dense polyline —
   * not separate curve pieces stitched together, which avoids curvature
   * mismatches at a joint (two pieces can share the same tangent
   * DIRECTION while still bending at very different rates, and that
   * mismatch reads as a kink even when direction itself is continuous).
   *
   * The shape is calibrated against 8 real reference charts spanning
   * understable, stable, and overstable discs at both short and long
   * distances:
   *   - turnInfluence(t) = t^1.3 — eases in with zero slope right at the
   *     release (so it doesn't look like the throw was aimed off to the
   *     side from the very first inch) but still keeps growing across
   *     the WHOLE flight rather than leveling off early — an
   *     understable disc (big turn, mild fade) shows a brief straight
   *     stem, then a continuous rightward lean that keeps building
   *     until very late, hooking back only slightly at the very tip.
   *   - fadeInfluence(t) = t^6 — stays essentially at zero through most
   *     of the flight and pulls back sharply only in roughly the final
   *     10%. This is what makes an overstable, low-turn disc look
   *     almost perfectly straight for most of its length with a tight,
   *     late hook, rather than curving evenly the whole way down.
   *   - Whichever effect is larger dominates the overall shape: a
   *     big-turn/small-fade disc reads as a continuous bow with a slight
   *     hook-back at the tip; a small-turn/big-fade disc reads as a
   *     straight stem with a sharp hook near the end.
   *   - Glide scales the turn effect multiplicatively, not as an
   *     independent addition, so a disc with zero measured turn gets
   *     exactly zero rightward lean — no stray bump before the hook.
   */
  function buildFlightPath(speed, glide, turn, fade, w, h) {
    w = w || 300;
    h = h || 190;
    var marginTop = 20, marginBottom = 22;
    var topY = marginTop, bottomY = h - marginBottom;
    var cx = w / 2;
    var pxPerFoot = (bottomY - topY) / FC_MAX_FEET;

    var distanceFt = estimateDistanceFt(speed, glide);
    var flightPx = distanceFt * pxPerFoot;

    var turnAmt = Math.max(0, -turn);
    var fadeAmt = Math.max(0, fade);
    var speedNorm = Math.min(Math.max(speed, 1), 15) / 15;
    var glideNorm = Math.min(Math.max(glide, 1), 7) / 7;

    var flightRange = bottomY - topY;
    var maxDrift = flightRange * 0.30;
    var rightDrift = (turnAmt / 5) * maxDrift * (0.55 + 0.45 * speedNorm) * (1 + glideNorm * 0.3);
    var leftHook = (fadeAmt / 5) * maxDrift * 0.9;

    var SAMPLES = 32;
    var points = [];
    for (var i = 0; i <= SAMPLES; i++) {
      var t = i / SAMPLES;
      var y = bottomY - t * flightPx;
      var turnInfluence = Math.pow(t, 1.3);
      var fadeInfluence = Math.pow(t, 6);
      var x = cx + rightDrift * turnInfluence - leftHook * fadeInfluence;
      points.push({ x: x, y: y });
    }

    var d = "M " + points[0].x + "," + points[0].y;
    for (var j = 1; j < points.length; j++) {
      d += " L " + points[j].x + "," + points[j].y;
    }

    return {
      d: d,
      start: points[0],
      end: points[points.length - 1],
      distanceFt: distanceFt,
      topY: topY,
      bottomY: bottomY,
      pxPerFoot: pxPerFoot
    };
  }

  function stabilityPosition(turn, fade, hand) {
    // 0 = fully overstable, 1 = fully understable. turn+fade is the
    // standard rough stability approximation (more negative = more
    // understable, more positive = more overstable). RHBH baseline has
    // Overstable on the left / Understable on the right (matching the
    // path: turn drifts right, fade drifts left). LHBH mirrors the path
    // via CSS (row-reverse on the labels), so mirror this value too.
    var sum = turn + fade;
    var position = (5 - sum) / 10;
    position = Math.min(1, Math.max(0, position));
    return hand === "left" ? 1 - position : position;
  }

  var MOBILE_BREAKPOINT = 480;
  function chartHeight() {
    // The viewBox aspect ratio (not just its absolute size) controls
    // how tall the chart renders at any given container width, since
    // the SVG scales to fill its container width and height follows
    // from the ratio. A fixed 300x190 ratio looks fine on desktop but
    // reads as squished on narrow phone screens, so mobile gets a
    // taller ratio instead of just shrinking the same shape down.
    return window.innerWidth <= MOBILE_BREAKPOINT ? 260 : 190;
  }

  function renderOne(el) {
    var base = {
      speed: parseFloat(el.dataset.speed),
      glide: parseFloat(el.dataset.glide),
      turn: parseFloat(el.dataset.turn),
      fade: parseFloat(el.dataset.fade)
    };
    if ([base.speed, base.glide, base.turn, base.fade].some(isNaN)) return;

    var skill = el.dataset.skill || "intermediate";
    var hand = el.dataset.hand || "right";
    var flight = adjustForSkill(base, skill);

    // The printed Speed/Glide/Turn/Fade badges always show the
    // manufacturer's actual flight numbers — only the drawn path and the
    // stability marker shift with the skill toggle, as a visualization
    // aid, not a claim about the disc's real numbers.
    var marker = el.querySelector(".fc-stability__marker");
    if (marker) marker.style.left = (stabilityPosition(flight.turn, flight.fade, hand) * 100) + "%";

    var svg = el.querySelector(".fc-svg");
    if (!svg) return;

    var w = 300, h = chartHeight();
    var built = buildFlightPath(flight.speed, flight.glide, flight.turn, flight.fade, w, h);
    var pathD = built.d;
    var start = built.start;
    var cx = w / 2;

    var gridLines = "";
    for (var ft = 100; ft <= 500; ft += 100) {
      var y = built.bottomY - ft * built.pxPerFoot;
      gridLines += '<line class="fc-grid-line" x1="30" y1="' + y + '" x2="' + (w - 12) + '" y2="' + y + '" />';
      gridLines += '<text class="fc-grid-label" x="4" y="' + (y + 3) + '">' + ft + '</text>';
    }

    // right-hand-backhand is the reference orientation; left-hand mirrors
    // everything path-related across the vertical center line
    var mirrorOpen = hand === "left" ? '<g transform="translate(' + w + ',0) scale(-1,1)">' : "<g>";
    var mirrorClose = "</g>";

    // teepad is centered on the path's start point (its vertical middle,
    // not its edge) — drawn before the path so the line sits in front of it
    var teepadW = 11, teepadH = 18;
    var teepadX = cx - teepadW / 2;
    var teepadY = start.y - teepadH / 2;

    svg.setAttribute("viewBox", "0 0 " + w + " " + h);
    svg.innerHTML =
      '<line class="fc-grid-line fc-grid-line--center" x1="' + cx + '" y1="' + built.topY + '" x2="' + cx + '" y2="' + (built.bottomY - 100 * built.pxPerFoot) + '" />' +
      gridLines +
      mirrorOpen +
      '<rect class="fc-teepad" x="' + teepadX + '" y="' + teepadY + '" width="' + teepadW + '" height="' + teepadH + '" />' +
      '<path class="fc-path" d="' + pathD + '" />' +
      mirrorClose;
  }

  function renderAll() {
    document.querySelectorAll("[data-flight-chart]").forEach(renderOne);
  }

  function initToggles(el) {
    el.querySelectorAll("[data-fc-toggle]").forEach(function (group) {
      var toggleKey = group.getAttribute("data-fc-toggle"); // "hand" or "skill"
      group.querySelectorAll(".fc-toggle-btn").forEach(function (btn) {
        btn.addEventListener("click", function () {
          group.querySelectorAll(".fc-toggle-btn").forEach(function (b) {
            b.classList.remove("is-active");
            b.setAttribute("aria-pressed", "false");
          });
          btn.classList.add("is-active");
          btn.setAttribute("aria-pressed", "true");
          el.dataset[toggleKey] = btn.getAttribute("data-value");
          renderOne(el);
        });
      });
    });
  }

  function initAll() {
    document.querySelectorAll("[data-flight-chart]").forEach(function (el) {
      initToggles(el);
      renderOne(el);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  // Re-init if a chart is swapped in dynamically (variant change, quick-view
  // modal, infinite-scroll collection grid, section re-render in the editor)
  document.addEventListener("shopify:section:load", initAll);

  var resizeTimer;
  function debouncedSync() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      renderAll();
    }, 150);
  }
  window.addEventListener("resize", debouncedSync);

  window.FlightChart = { renderAll: renderAll, renderOne: renderOne, initAll: initAll };
})();
